// Generated by tutorials/record.cjs from tutorials/articles/h72-forecasting.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h72 --text-only` (wording) or
// `node tutorials/record.cjs h72` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'forecasting',
  catalogId: 'h72',
  title: 'Forecasting extra servings',
  summary: 'Tell the kitchen how many extra servings to make for a date and meal, on top of the residents\' trays: the Production Tally adds them to its counts.',
  audience: 'Dietary managers and production staff at facilities that use production',
  area: 'kitchen',
  learn: [
    'What a forecast is and where its numbers go',
    'Make forecasts for one or more days from the menu and a census',
    'Change a quantity, add an item and save',
    'How Forecasting differs from Demand Forecasting',
  ],
  keywords: [
    'forecasting',
    'forecast',
    'extra servings',
    'extras',
    'production tally',
    'production',
    'census',
    'primary',
    'alternate',
    'quantity',
    'qty',
    'menu category',
    'kitchen',
  ],
  steps: [
    {
      id: 'open',
      part: 'Read a forecast',
      heading: 'Open Forecasting',
      text: 'Select Kitchen in the menu on the left, then Forecasting.\n\nEach row is one forecast: a Date and a Mealtime, newest first. Select the arrow at the start of a row to open it.',
      media: {
        image: {
          src: '/help/forecasting/open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/forecasting/open.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.4,
        },
        alt: 'The Forecasting list with forecasts for today and yesterday, Lunch and Dinner, and today\'s Lunch opened below it.',
      },
    },
    {
      id: 'tree',
      part: 'Read a forecast',
      heading: 'Read what it adds',
      text: 'Inside are the day\'s menu categories that your facility forecasts, such as Entree and Dessert, in bold. Under each are that meal\'s choices, with the items served with them indented below.\n\nQty is how many extra servings of that item to make. The Production Tally adds them to its counts for that date and meal, under the item\'s menu category, on top of what the residents\' trays need.',
      media: {
        image: {
          src: '/help/forecasting/tree.webp',
          width: 1280,
          height: 378,
        },
        alt: 'Today\'s Lunch forecast: Entree with Baked Chicken Breast and Chicken Gravy at Qty 30, Beef Stroganoff and Buttered Egg Noodles at 10; Dessert with Apple Crisp at 30 and Sugar Free Vanilla Pudding at 10.',
      },
    },
    {
      id: 'new',
      part: 'Make forecasts',
      heading: 'Pick the days, census and meals',
      text: 'Select New. The Add New Forecasting box shows the menu in use.\n\nPick the first Date and the last day in To; one box can cover up to one menu cycle. Census starts at your facility\'s recipe census. Change it and Primary and Alternate are worked out for you: about three quarters and one quarter, each to the nearest 5. Leave checked the meals you want forecasts for.',
      media: {
        image: {
          src: '/help/forecasting/new.webp',
          width: 708,
          height: 706,
        },
        clip: {
          src: '/help/forecasting/new.mp4',
          width: 708,
          height: 706,
          durationSec: 6,
        },
        alt: 'The Add New Forecasting box for Fall/Winter Cycle 2026: Date tomorrow, To the day after, Census 50, Primary 40, Alternate 10, with Lunch and Dinner checked.',
      },
    },
    {
      id: 'made',
      part: 'Make forecasts',
      heading: 'Make them',
      text: 'Select Save. A forecast is made for each day and meal, from that day\'s menu: the first choice in each menu category gets the Primary number, the second gets the Alternate, and any others start at 0.',
      media: {
        image: {
          src: '/help/forecasting/made.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/forecasting/made.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.3,
        },
        alt: 'The Forecasting list with four new forecasts at the top, tomorrow\'s Lunch opened: Baked Chicken Breast and Apple Crisp at 40, Beef Stroganoff and Sugar Free Vanilla Pudding at 10.',
      },
    },
    {
      id: 'qty',
      part: 'Change and save a forecast',
      heading: 'Change a quantity',
      text: 'Select a Qty and type the number of extra servings. The menu category rows have no Qty.\n\nNothing is saved yet: Unsaved changes shows at the bottom of the page until you select Save.',
      media: {
        image: {
          src: '/help/forecasting/qty.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/forecasting/qty.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.8,
        },
        alt: 'Tomorrow\'s Lunch forecast with Beef Stroganoff changed to 15, and Unsaved changes at the bottom of the page.',
      },
    },
    {
      id: 'add',
      part: 'Change and save a forecast',
      heading: 'Add an item',
      text: 'To forecast an item that is not listed, select the row it goes under and choose a level: Level 2 adds a choice, Level 3 an item under a choice, and Level 4 an item under that.\n\nA new row reads [Select Item]. Select its Description, pick the item, and type its Qty.',
      media: {
        image: {
          src: '/help/forecasting/add.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/forecasting/add.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'Tomorrow\'s Lunch forecast with a new row, Dinner Roll at Qty 25, under Baked Chicken Breast.',
      },
    },
    {
      id: 'save',
      part: 'Change and save a forecast',
      heading: 'Save',
      text: 'Select Save at the top. Your changes go to the Production Tally from now on.\n\nTo remove a row and everything under it, or a whole forecast, select it and then Delete, and then Save.',
      media: {
        image: {
          src: '/help/forecasting/save.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/forecasting/save.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.7,
        },
        alt: 'The Forecasting page after Save, with Save greyed out, no Unsaved changes, and the message Forecasting saved.',
      },
    },
  ],
  tips: [
    'Make forecasts for the whole menu cycle at once: the To date can be up to one cycle after the start date.',
    'Change Census and Primary and Alternate are worked out for you. You can still type your own Primary and Alternate.',
    'Changes on this page are kept until you select Save, and Unsaved changes shows at the bottom until you do. If you leave first, Dietech Online asks whether to save them.',
  ],
  notes: [
    'Forecasting is for facilities that use production. Changing forecasts needs a Forecasting level of 2 or more on your role; at level 1 you can only look.',
    'The menu categories a forecast covers are the ones listed under Bulk 1st Choice Inclusion in Facility Settings, Menu Category Priority, for that meal. If a forecast comes out empty, the day\'s menu has none of them.',
    'Making a forecast for a date and meal that already has one asks before replacing it.',
    'Demand Forecasting, under Reports, is a different page: it estimates meal counts by diet from past service and changes nothing. Forecasting is where you set the extra servings the Production Tally adds.',
  ],
  related: ['bulk-order', 'running-a-report', 'menu-cycles', 'facility-setup', 'roles-and-access'],
  recorded: '2026-11-03',
};

export default article;
